import { useState, useEffect, useMemo, useCallback } from 'react';
import { Form, Upload } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { createJDProgress, createReviseJDProgress, systemUploadInsert, systemUpload, getRoleReportingView } from 'Api/apiService';
import { buildFieldConfigMap } from 'Utils/formatting';
import useAuthStore from 'Store/useAuthStore';
import useEntitlementsStore from 'Store/useEntitlementsStore';
import { showNotification } from 'Store/actions/notificationActions';
import { showToast } from 'Store/actions/profileListActions';
import { validateUploadedFile } from 'Utils/fileValidation';
import { isTokenValid } from 'Utils/tokenValidation';
import { handleUnauthorizedSession } from 'Utils/authSession';
import { ACCESS_TOKEN_KEY } from 'constants/localStorage.constants';
import {
    useOptions, handleAutoSelect,
    getDuplicateDetectionConfig, isFieldDisabledByDuplicateDetection,
    isSelectAll, expandSentinels, filterSelected,
} from './helpers';

import { JD_ACTION } from 'constants/jobs.constants';

// ── Pure helpers ──────────────────────────────────────────────────────────────

// Job codes are compared normalised -- the catalogue and the JD can differ by type or whitespace.
const sameJobCode = (a, c) => String(a ?? '').trim() === String(c ?? '').trim();

// Normalize a multi-select field value to an array.
// API/job snapshots may store it as a comma-separated string or a single value.
const toMultiValue = (v) => {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string' && v.length) return v.split(',').map(s => s.trim()).filter(Boolean);
    return v ? [v] : [];
};

// Resolve a stored multi-select value (a comma-joined string) into an array of
// real option names. Option names can themselves contain commas so a plain comma 
// split would break one name into several. We match against the known option list with a greedy
// longest-first match, and only fall back to a comma split if nothing matches.
const resolveMulti = (stored, optionNames = []) => {
    if (Array.isArray(stored)) return stored;
    if (typeof stored !== 'string' || !stored.trim()) return [];
    const text = stored.trim();

    // Whole string is exactly one option (covers names containing commas).
    if (optionNames.includes(text)) return [text];

    // Greedy: consume the longest option name at the current position, repeat.
    const sorted = [...optionNames].sort((a, b) => b.length - a.length);
    const result = [];
    let rest = text;
    while (rest.length) {
        const current = rest;
        const match = sorted.find(name => current === name || current.startsWith(`${name},`));
        if (!match) break;
        result.push(match);
        rest = current.slice(match.length).replace(/^\s*,\s*/, '');
    }
    if (result.length) return result;

    // No option list (or no match) — fall back to the naive comma split.
    return text.split(',').map(s => s.trim()).filter(Boolean);
};

const joinMulti = (v) =>
    (Array.isArray(v) ? v.filter(x => !isSelectAll(x)).join(', ') : (v ?? ''));

const buildPayload = ({ data, email, orgName, orgIndustry, businessContext, userRole, jobGradeLabel }) => ({
    role: data.roleTitle ?? data.altRoleTitle ?? '',
    alt_role_title: data.altRoleTitle,
    email,
    user_action_role: userRole, // user initiated flow using this role access
    company: orgName,
    industry: orgIndustry,
    business_unit: data.businessUnit,
    legal_entity: data.legalEntity,
    reportingTo: data.reportingTo ?? '',
    function: joinMulti(data.functionDepartment),
    sub_function: joinMulti(data.subFunction),
    specialty: joinMulti(data.specialty),
    level: jobGradeLabel ?? data.jobGrade,
    role_type: data.roleType,
    work_environment: data.workEnvironment,
    business_context: businessContext,
    job_code: data.jobCode,
    job_title_for_jd: data.jobTitleForJd,
    location: Array.isArray(data.location) ? data.location.join(', ') : data.location,
    role_context: data.roleContext ?? '',
    // Mutually exclusive — Is Apex takes precedence if both were somehow set,
    // since an apex role has no one to report to. Sent as sb_unique_id, not
    // job_id/job_code, matching the Admin Panel Role Reporting feature's
    // working contract (manager_sb_unique_id in useRoleReporting.js).
    ...(data.isApexRole
        ? { is_apex_role: true }
        : data.roleReportingToSbUniqueId
            ? { role_reporting_to_sb_unique_id: data.roleReportingToSbUniqueId }
            : {}),
});



const toFormData = (payload, extra = {}) => {
    const fd = new FormData();
    Object.entries({ ...payload, ...extra }).forEach(([key, value]) => {
        if (value !== undefined && value !== null) fd.append(key, value);
    });
    return fd;
};

// ── Hook ──────────────────────────────────────────────────────────────────────

const useJdForm = ({
    jdActionSelection = JD_ACTION.CREATE,
    isDrawerOpen,
    toggleDrawer,
    setPendingJdId,
    versionData = [],
    selectedJdVersion,
    handleVersionChange,
    setJobTaskId = () => { },
    setDrawerSubmitLoading,
    onUploadSuccess,
}) => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const email = useAuthStore((s) => s.email);
    const orgMetaData = useAuthStore((s) => s.orgMetaData);
    const profileRole = useEntitlementsStore((s) => s.profileRole);

    const ensureValidSessionForSubmit = useCallback(() => {
        const token = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (isTokenValid(token, 'CreateJDSubmit')) return true;

        dispatch(showNotification('Your session has expired. Please login again.', 'error'));
        setDrawerSubmitLoading?.(false);
        setUploadSubmitting(false);
        setLoading(false);
        handleUnauthorizedSession();
        return false;
    }, [dispatch, setDrawerSubmitLoading]);

    const orgStructure = useMemo(() => ({
        organization_structure: orgMetaData?.organization_structure || []
    }), [orgMetaData]);

    const orgName = orgMetaData?.org_name || ''
    const orgIndustry = orgMetaData?.org_industry?.[0] || ''
    const businessContext = orgMetaData.business_context?.industry || ''

    // ── Local state ────────────────────────────────────────────────────────────

    const [selectedJobFields, setSelectedJobFields] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [uploadSubmitting, setUploadSubmitting] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [fileValidationError, setFileValidationError] = useState(null);
    const [availableBusinessUnits, setAvailableBusinessUnits] = useState([]);
    const [availableFunctions, setAvailableFunctions] = useState([]);
    const [availableSubFunctions, setAvailableSubFunctions] = useState([]);
    const [availableSpecialties, setAvailableSpecialties] = useState([]);
    // Role Reporting To options — matches the Admin Panel Role Reporting
    // feature's contract (useRoleReporting.js): manager is identified by
    // sb_unique_id, not job_id/job_code, since a role can be revised into a
    // new JD version that keeps the same job_id/job_code but gets a new
    // sb_unique_id — the backend resolves the manager by that column.
    const [roleReportingToOptions, setRoleReportingToOptions] = useState([]);
    // ── Watched form values ────────────────────────────────────────────────────

    const legalEntity = Form.useWatch('legalEntity', form);
    const businessUnit = Form.useWatch('businessUnit', form);
    const functionDepartment = Form.useWatch('functionDepartment', form);
    const subFunction = Form.useWatch('subFunction', form);
    const specialty = Form.useWatch('specialty', form);
    const roleTitle = Form.useWatch('roleTitle', form);
    const altRoleTitle = Form.useWatch('altRoleTitle', form);
    const jobTitleForJd = Form.useWatch('jobTitleForJd', form);
    const jobGrade = Form.useWatch('jobGrade', form);
    const roleType = Form.useWatch('roleType', form);
    const workEnvironment = Form.useWatch('workEnvironment', form);
    const jobCode = Form.useWatch('jobCode', form);
    const location = Form.useWatch('location', form);
    const roleReportingToSbUniqueId = Form.useWatch('roleReportingToSbUniqueId', form);
    const isApexRole = Form.useWatch('isApexRole', form);
    // ── Options ────────────────────────────────────────────────────────────────

    const catalogueJobOptions = useOptions('jobs', null, orgMetaData);
    const roleTypeOptions = useOptions('company_structure', 'role_type', orgMetaData);
    const jobGradeOptions = useOptions('company_structure', 'job_grade', orgMetaData);
    const workEnvironmentOptions = useOptions('company_structure', 'Work_Environment', orgMetaData);
    const legalEntities = useOptions('legal_entities', null, orgStructure);
    const reportingToOptions = useOptions('company_structure', 'report_to', orgMetaData);

    // The version being revised -- resolved as the prefill does.
    const reviseJob = useMemo(() => {
        if (jdActionSelection !== JD_ACTION.REVISE || !versionData?.length || !selectedJdVersion) return null;
        return versionData.find(v => String(v.version) === String(selectedJdVersion)) || null;
    }, [jdActionSelection, versionData, selectedJdVersion]);

    // The exact option value Job Code must hold to resolve: the catalogue's own value when it
    // matches, else the JD's code (appended below). Both call sites must use this, not the raw code.
    const reviseJobCodeValue = useMemo(() => {
        const code = reviseJob?.job_code;
        if (!code) return null;
        return catalogueJobOptions.find(o => sameJobCode(o.value, code))?.value ?? code;
    }, [catalogueJobOptions, reviseJob]);

    // A job code absent from the catalogue would be dropped by SbSelect and reported as a user clear, resetting the form.
    const jobOptions = useMemo(() => {
        const code = reviseJob?.job_code;
        if (!code || !catalogueJobOptions.length) return catalogueJobOptions;
        if (catalogueJobOptions.some(o => sameJobCode(o.value, code))) return catalogueJobOptions;

        const raw = reviseJob.job_level ? String(reviseJob.job_level) : '';
        // Strip only a numeric prefix, so a title such as "Manager - Senior" is left intact.
        const bare = /^\d+\s*-\s*/.test(raw) ? raw.replace(/^\d+\s*-\s*/, '') : raw;
        const grade = jobGradeOptions.find(o => o.value === bare);
        const [num, ...title] = String(grade?.label ?? raw).split(' - ');
        const level = grade && /^\d+$/.test(num)
            ? [num.padStart(2, '0'), ...title].join(' - ')
            : raw;

        // Mirrors the catalogue's `<job_code> - <job_family_name>`: code USAPCM10 + level "Associate"
        // + sub-function "PMG - Core Product Management" -> "USAPCM10 - 10 - Associate - PMG - Core Product Management".
        const label = [code, level, reviseJob.sub_function].filter(Boolean).join(' - ');
        return [...catalogueJobOptions, { value: code, label }];
    }, [catalogueJobOptions, jobGradeOptions, reviseJob]);

    // ── Derived values ─────────────────────────────────────────────────────────

    const selectedEntity = useMemo(() => {
        if (!legalEntity) return null;
        return orgStructure?.organization_structure?.find(
            item => item.legal_entity === legalEntity,
        ) || null;
    }, [legalEntity, orgStructure]);

    const filteredOfficeLocations = useMemo(() => {
        if (!legalEntity || !orgStructure?.organization_structure) return [];
        if (!Array.isArray(selectedEntity?.locations)) return [];
        return selectedEntity.locations.map(loc => ({ value: loc.name, label: loc.name }));
    }, [legalEntity, selectedEntity]);

    const duplicateDetectionConfig = useMemo(
        () => getDuplicateDetectionConfig(orgMetaData),
        [orgMetaData],
    );

    const canAutoGenerateContext = useMemo(() => Boolean(
        roleTitle && altRoleTitle && jobTitleForJd &&
        legalEntity && businessUnit && functionDepartment?.length && subFunction?.length &&
        (Array.isArray(location) ? location.length > 0 : location) &&
        jobGrade && roleType && workEnvironment && jobCode,
    ), [roleTitle, altRoleTitle, jobTitleForJd, legalEntity, businessUnit, functionDepartment, subFunction, location, jobGrade, roleType, workEnvironment, jobCode]);

    const versionOptions = useMemo(
        () => versionData.map(v => ({ value: String(v.version), label: `Version - ${v.version}` })),
        [versionData],
    );

    // ── Field config ───────────────────────────────────────────────────────────
    // Derive field config directly from orgMetaData — no intermediate state needed.
    // buildFieldConfigMap(orgMetaData, 'create_jd') targets only the create_jd
    // ui_element, so labels/visibility come from the correct section and are never
    // polluted by fields from the req_no or job_code sections.
    const fieldConfigMap = useMemo(
        () => buildFieldConfigMap(orgMetaData, 'create_jd'),
        [orgMetaData],
    );

    // Pre-compute all field configs to avoid repeated map lookups in JSX
    const fields = useMemo(() => ({
        jobCode:       fieldConfigMap['job_code']        || {},
        roleTitle:     fieldConfigMap['role']            || {},
        altRoleTitle:  fieldConfigMap['alt_role_title']  || {},
        jobTitleForJd: fieldConfigMap['job_title_for_jd']|| {},
        legalEntity:   fieldConfigMap['legal_entity']    || {},
        businessUnit:  fieldConfigMap['business_unit']   || {},
        function:      fieldConfigMap['function']        || {},
        subFunction:   fieldConfigMap['sub_function']    || {},
        specialty: fieldConfigMap['specialty']      || {},
        roleType:      fieldConfigMap['role_type']       || {},
        level:         fieldConfigMap['level']           || {},
        reportingTo:   fieldConfigMap['reporting_to']    || {},
        workEnvironment: fieldConfigMap['work_environment'] || {},
        location:      fieldConfigMap['location']        || {},
        roleContext:   fieldConfigMap['role_context']    || {},
    }), [fieldConfigMap]);

    const isFieldDisabled = useCallback((fieldName) => {
        if (jdActionSelection !== JD_ACTION.REVISE) return false;
        if (fieldName === 'jobCode') return true;
        return isFieldDisabledByDuplicateDetection(fieldName, duplicateDetectionConfig);
    }, [jdActionSelection, duplicateDetectionConfig]);

    const jobGradeValue = useCallback((level) =>
        typeof level === 'string' && level.includes(' - ')
            ? level.split(' - ').slice(1).join(' - ')
            : level,
        []);

    // ── Effects ─────────────────────────────────────────────────────

    // Reset pending JD id on mount

    useEffect(() => {
        setPendingJdId('');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Role Reporting To options — fetched on open from the same endpoint the
    // Admin Panel Role Reporting feature uses, keyed by sb_unique_id (a JD
    // version's own id), since that's the column the backend actually
    // resolves the chosen manager against.
    useEffect(() => {
        if (!isDrawerOpen || jdActionSelection === JD_ACTION.REVISE) return;
        let cancelled = false;
        (async () => {
            try {
                const rows = await getRoleReportingView(email);
                if (cancelled) return;
                setRoleReportingToOptions(
                    (Array.isArray(rows) ? rows : [])
                        .filter(r => r?.sb_unique_id)
                        .map(r => ({
                            value: r.sb_unique_id,
                            label: r.job_code ? `${r.job_title} (${r.job_code})` : r.job_title || r.sb_unique_id,
                        }))
                );
            } catch (err) {
                if (!cancelled) setRoleReportingToOptions([]);
            }
        })();
        return () => { cancelled = true; };
    }, [isDrawerOpen, jdActionSelection, email]);

    // Reset form, file, and error when drawer closes — prevents stale state leaking into the next session
    useEffect(() => {
        if (isDrawerOpen) return;
        form.resetFields();
        setUploadedFile(null);
        setUploadError(null);
        setFileValidationError(null);
    }, [isDrawerOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Pre-populate form in revise mode
    useEffect(() => {
        if (!isDrawerOpen || jdActionSelection !== JD_ACTION.REVISE || !versionData?.length || !selectedJdVersion) return;
        if (!orgStructure?.organization_structure.length) return; // wait for org structure so cascade can populate options
        const selected = versionData.find(v => String(v.version) === String(selectedJdVersion));
        if (!selected) return;

        const rawLocation = selected.location;

        // Resolve the dependent option lists up front so multi-select values
        const entity = orgStructure.organization_structure.find(
            item => item.legal_entity === selected.legal_entity,
        );
        const businessUnits = entity?.business_units || [];
        const selectedBU = businessUnits.find(u => u.business_unit === selected.business_unit);
        const functions = selectedBU?.functions || [];

        // Match the stored location back to the entity's location options.
        // Remote keeps the raw single value; otherwise recover the multi-select
        // from the comma-joined stored string (option names may themselves contain commas).
        const locationValue =
            selected.work_environment === "Remote"
                ? (rawLocation || "")
                : resolveMulti(rawLocation, (entity?.locations || []).map(l => l.name));

        const funcValues = resolveMulti(selected.function, functions.map(f => f.function));

        const subFunctions = functions
            .filter(f => funcValues.includes(f.function))
            .flatMap(f => f.sub_functions || []);
        const uniqueSubFunctions = Array.from(new Map(subFunctions.map(sf => [sf.sub_function, sf])).values());

        const subFuncValues = resolveMulti(selected.sub_function, uniqueSubFunctions.map(sf => sf.sub_function));

        const specialties = uniqueSubFunctions
            .filter(sf => subFuncValues.includes(sf.sub_function))
            .flatMap(sf => sf.specialties || []);
        const uniqueSpecialties = Array.from(new Map(specialties.map(s => [s.specialty, s])).values());

        const specialtyValues = resolveMulti(selected.specialty, uniqueSpecialties.map(s => s.specialty));

        form.setFieldsValue({
            jobCode: selected.job_code || '',
            roleTitle: selected.job_title || '',
            altRoleTitle: selected.alt_role_title || '',
            jobTitleForJd: selected.job_title_for_jd || '',
            legalEntity: selected.legal_entity || '',
            businessUnit: selected.business_unit || '',
            functionDepartment: funcValues,
            subFunction: subFuncValues,
            specialty: specialtyValues,
            roleType: selected.role_type || '',
            jobGrade: jobGradeValue(selected.job_level),
            workEnvironment: selected.work_environment || '',
            location: locationValue,
            roleContext: selected.role_context || '',
        });

        // Populate cascade options so the dependent Selects display correctly.
        setAvailableBusinessUnits(
            Array.from(new Map(businessUnits.map(bu => [bu.business_unit, bu])).values())
                .map(bu => ({ value: bu.business_unit, label: bu.business_unit, description: bu.business_unit_description })),
        );
        setAvailableFunctions(functions.map(f => ({ value: f.function, label: f.function })));
        setAvailableSubFunctions(uniqueSubFunctions.map(sf => ({ value: sf.sub_function, label: sf.sub_function })));
        setAvailableSpecialties(uniqueSpecialties.map(s => ({ value: s.specialty, label: s.specialty })));
    }, [isDrawerOpen, jdActionSelection, versionData, selectedJdVersion, orgStructure]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-generate role context when all required fields are filled
    useEffect(() => {
        if (!canAutoGenerateContext) return;
        generateRoleContextData(); 
    }, [canAutoGenerateContext]); // eslint-disable-line react-hooks/exhaustive-deps

    // Role Reporting To / Is Apex are mutually exclusive — an apex role has
    // no one to report to, so picking one clears the other.
    useEffect(() => {
        if (isApexRole && roleReportingToSbUniqueId) form.setFieldValue('roleReportingToSbUniqueId', undefined);
    }, [isApexRole]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (roleReportingToSbUniqueId && isApexRole) form.setFieldValue('isApexRole', false);
    }, [roleReportingToSbUniqueId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset location when work environment changes (prevents mode/value mismatch).
    // Skip in REVISE mode — location is pre-populated and the user can clear it manually.
    useEffect(() => {
        if (jdActionSelection === JD_ACTION.REVISE) return;
        if (!workEnvironment) return;
        form.setFields([{ name: 'location', value: undefined, errors: [] }]);
    }, [workEnvironment, jdActionSelection]); // eslint-disable-line react-hooks/exhaustive-deps

    // Cascade business units / functions / sub-functions
    useEffect(() => {
        const businessUnits = jdActionSelection === JD_ACTION.REVISE 
            ? selectedEntity?.business_units || []
            : orgStructure?.organization_structure?.flatMap(e => e.business_units || []) || [];
            

        const selectedBU = businessUnits.find(u => u.business_unit === businessUnit);
        const uniqueBusinessUnits = Array.from(
            new Map(businessUnits.map(bu => [bu.business_unit, bu])).values()
        );

        setAvailableBusinessUnits(
            uniqueBusinessUnits.map(bu => ({
                value: bu.business_unit,
                label: bu.business_unit,
                description: bu.business_unit_description,
            }))
        );

        if (!selectedEntity) return;

        const isRevise = jdActionSelection === JD_ACTION.REVISE;  

        if (businessUnit && !selectedBU) {
            if (!isRevise) {
                form.setFieldValue('businessUnit', '');
                form.setFieldValue('functionDepartment', []);
                form.setFieldValue('subFunction', []);
            }
            return;
        }

        const functions = selectedBU?.functions || [];
        const functionOpts = functions.map(dept => ({ value: dept.function, label: dept.function }));
        setAvailableFunctions(functionOpts);

        // function is multi-select — expand "ALL - All" to every real function, then
        // drop any selections no longer available. Expansion runs in every mode
        // (incl. revise); stale-dropping is create-only so saved values aren't wiped.
        const selectedFuncRaw = toMultiValue(functionDepartment);
        const funcHadSentinel = selectedFuncRaw.some(isSelectAll);
        const validFuncs = filterSelected(expandSentinels(selectedFuncRaw, functionOpts), functionOpts);
        const funcChanged =
            validFuncs.length !== selectedFuncRaw.length ||
            !selectedFuncRaw.every(v => validFuncs.includes(v));
        if (funcChanged && (!isRevise || funcHadSentinel)) {
            form.setFieldValue('functionDepartment', validFuncs);
        }

        // Aggregate sub-functions across every selected function
        const selectedFuncs = functions.filter(dept => validFuncs.includes(dept.function));
        const subFunctions = selectedFuncs.flatMap(f => f.sub_functions || []);
        const uniqueSubFunctions = Array.from(new Map(subFunctions.map(sf => [sf.sub_function, sf])).values());
        const subFunctionOpts = uniqueSubFunctions.map(sf => ({ value: sf.sub_function, label: sf.sub_function }));
        setAvailableSubFunctions(subFunctionOpts);

        // sub-function is multi-select — expand "ALL - All" (every mode) + drop stale
        // selections (create-only, so saved revise values aren't wiped on load)
        const selectedSubRaw = toMultiValue(subFunction);
        const subHadSentinel = selectedSubRaw.some(isSelectAll);
        const validSubs = filterSelected(expandSentinels(selectedSubRaw, subFunctionOpts), subFunctionOpts);
        const subChanged =
            validSubs.length !== selectedSubRaw.length ||
            !selectedSubRaw.every(v => validSubs.includes(v));
        if (subChanged && (!isRevise || subHadSentinel)) {
            form.setFieldValue('subFunction', validSubs);
        }
    }, [businessUnit, functionDepartment, subFunction, selectedEntity, jdActionSelection]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const isRevise = jdActionSelection === JD_ACTION.REVISE;
        const businessUnits = isRevise
            ? selectedEntity?.business_units || []
            : orgStructure?.organization_structure?.flatMap(e => e.business_units || []) || [];

        const selectedBU = businessUnits.find(u => u.business_unit === businessUnit);
        const functions = selectedBU?.functions || [];
        const functionOpts = functions.map(f => ({ value: f.function, label: f.function }));

        // Expand "ALL - All" sentinels so child options aggregate across all real picks
        const selectedFuncList = expandSentinels(toMultiValue(functionDepartment), functionOpts);
        const selectedFuncs = functions.filter(f => selectedFuncList.includes(f.function));
        const allSubFuncs = selectedFuncs.flatMap(f => f.sub_functions || []);
        const subFunctionOpts = Array.from(new Map(allSubFuncs.map(sf => [sf.sub_function, sf])).values())
            .map(sf => ({ value: sf.sub_function, label: sf.sub_function }));
        const selectedSubList = expandSentinels(toMultiValue(subFunction), subFunctionOpts);

        // Aggregate specialties across every selected function/sub-function pair
        const selectedSubFuncs = allSubFuncs.filter(sf => selectedSubList.includes(sf.sub_function));
        const specialties = selectedSubFuncs.flatMap(sf => sf.specialties || []);
        const uniqueSpecialties = Array.from(new Map(specialties.map(s => [s.specialty, s])).values());
        const opts = uniqueSpecialties.map(s => ({ value: s.specialty, label: s.specialty }));
        setAvailableSpecialties(opts);

        // specialty is multi-select — expand "ALL - All" (every mode) + drop stale
        // selections (create-only, so saved revise values aren't wiped on load)
        const selectedSpecRaw = toMultiValue(specialty);
        const specHadSentinel = selectedSpecRaw.some(isSelectAll);
        const validSpecs = filterSelected(expandSentinels(selectedSpecRaw, opts), opts);
        const specChanged =
            validSpecs.length !== selectedSpecRaw.length ||
            !selectedSpecRaw.every(v => validSpecs.includes(v));
        if (specChanged && (!isRevise || specHadSentinel)) {
            form.setFieldValue('specialty', validSpecs);
        }
    }, [businessUnit, functionDepartment, subFunction, selectedEntity, orgStructure, specialty, jdActionSelection]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep the selected-job snapshot (role_full_object) in sync with live form edits.
    // Functional update mutates the existing object in place; no-op until a job is selected.
    useEffect(() => {
        setSelectedJobFields(prev => prev && {
            ...prev,
            business_unit: businessUnit,
            function: joinMulti(functionDepartment),
            sub_function: joinMulti(subFunction),
            specialty: joinMulti(specialty),
            role_type: roleType,
            work_environment: workEnvironment,
            legal_entity: legalEntity,
            job_code: jobCode,
            job_title: roleTitle,
            // level: form holds the bare grade title — restore "NN - Title" from the option label
            level: jobGradeOptions.find(o => o.value === jobGrade)?.label ?? prev.level,
            // location: keep the snapshot value while the form's is transiently empty (work-env reset)
            location: (Array.isArray(location) ? location.join(', ') : location) || prev.location,
        });
    }, [businessUnit, functionDepartment, subFunction, specialty, roleType, workEnvironment, legalEntity, jobCode, roleTitle, jobGrade, location]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── API: Generate role context ─────────────────────────────────────────────

    const generateRoleContextData = async () => {
        setLoading(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 600));
            const apiModule = await import('Api/apiService');
            const generateRoleContext = apiModule.generateRoleContext;
            if (!generateRoleContext) return;
            const response = await generateRoleContext({
                job_code: jobCode, alt_role_title: altRoleTitle,
                job_title_for_jd: jobTitleForJd, legal_entity: legalEntity,
                business_unit: businessUnit, function: joinMulti(functionDepartment),
                sub_function: joinMulti(subFunction), role_type: roleType,
                level: jobGrade, work_environment: workEnvironment, location,
            });
            if (!response) throw new Error('No response received');
            form.setFieldValue('roleContext', response.role_context);
        } catch (err) {
            dispatch(showNotification('Error generating role context.', 'error'));
        } finally {
            setLoading(false);
        }
    };

    const submitRevise = async (formData) => {
        setLoading(true);
        formData.append('original_jd_id', versionData?.[0]?.sb_unique_id);
        try {
            const resp = await createReviseJDProgress(formData);
            if (!resp) throw new Error('No response received from server');
            if (!resp.task_id) throw new Error('No task_id in response');
            setLoading(false);
            setPendingJdId(resp.job_id);
            toggleDrawer();
            navigate('/jobs-library', {
                state: resp.task_id ? { task_id: resp.task_id, jd_id: '' } : '',
            });
        } catch (err) {
            setLoading(false);
            dispatch(showNotification('Error uploading JD.', 'error'));
            toggleDrawer();
        }
    };

    const submitUpload = async (formData) => {
        setUploadSubmitting(true);
        setUploadError(null);
        try {
            const insertResp = await systemUploadInsert(formData);
            if (!insertResp?.job_id) throw new Error('No job_id in insert response');

            setUploadSubmitting(false);
            toggleDrawer();
            dispatch(showToast('JD submitted. Processing usually takes 3–4 minutes — results will be visible in Jobs Library.', 'success'));
            onUploadSuccess?.();

            // Second API runs in background — no need to block the user.
            systemUpload(insertResp.job_id, email).catch(() => {
                dispatch(showNotification('JD was submitted but background processing failed. Please contact support.', 'error'));
            });
        } catch (err) {
            setUploadSubmitting(false);
            const backendMsg = err?.response?.data?.message
                || err?.response?.data?.detail
                || err?.response?.data?.error;
            setUploadError(backendMsg || 'Failed to upload JD. Please try again.');
        }
    };

    const submitCreate = async (formData) => {
        setDrawerSubmitLoading(true);
        try {
            const resp = await createJDProgress(formData);
            if (resp?.task_id) {
                setPendingJdId(resp.job_id);
                setJobTaskId({ task_id: resp.task_id, job_id: resp.task_id });
            } else {
                setDrawerSubmitLoading(false);
                dispatch(showNotification(`Could not ${jdActionSelection} JD. Please retry.`, 'error'));
                toggleDrawer();
            }
        } catch (err) {
            setDrawerSubmitLoading(false);
            dispatch(showNotification(`Failed to ${jdActionSelection} JD.`, 'error'));
            toggleDrawer();
        }
    };

    // ── Form submit ────────────────────────────────────────────────────────────

    const onFinish = async (data) => {

        if (!ensureValidSessionForSubmit()) return;

        const jobGradeLabel = jobGradeOptions.find(o => o.value === data.jobGrade)?.label ?? data.jobGrade;
        const payload = buildPayload({ data, email, orgName, orgIndustry, businessContext, userRole: profileRole, jobGradeLabel });
        const formData = toFormData(payload);
        if (selectedJobFields) formData.append('role_full_object', JSON.stringify(selectedJobFields));

        if (jdActionSelection === JD_ACTION.REVISE) {
            await submitRevise(formData);
        } else if (jdActionSelection === JD_ACTION.UPLOAD) {
            if (uploadedFile) formData.append('file', uploadedFile);
            await submitUpload(formData);
        } else {
            await submitCreate(formData);
        }
    };

    // ── Handlers ───────────────────────────────────────────────────────────────

    const handleJobChange = useCallback((value) => {
        // Job Code is disabled in revise, so an unresolved value is never a real user clear.
        if (jdActionSelection === JD_ACTION.REVISE) {
            // Restore only when it resolves, else SbSelect clears it again next render and loops.
            if (reviseJobCodeValue && jobOptions.some(o => o.value === reviseJobCodeValue)) {
                form.setFieldValue('jobCode', reviseJobCodeValue);
            }
            return;
        }
        const selectedOption = jobOptions.find(o => o.value === value);
        handleAutoSelect({
            selectedOption,
            resetForm: () => form.resetFields(),
            setFieldValue: (name, val) => form.setFieldValue(name, val),
            jobGradeValue,
            setSelectedJobFields,
        });
        setUploadedFile(null);
        setUploadError(null);
        setFileValidationError(null);
    }, [jobOptions, form, jobGradeValue, jdActionSelection, reviseJobCodeValue]);

    const handleVersionChangeInput = useCallback((value) => {
        handleVersionChange(value);
        form.setFieldValue('location', []);
    }, [handleVersionChange, form]);

    // Validate file for security (magic bytes) and OCR readiness (text layer) before accepting it.
    const handleBeforeUpload = useCallback(async (file) => {
        const error = await validateUploadedFile(file);
        setFileValidationError(error || null);
        setUploadedFile(file);
        if (!error) setUploadError(null);
        return false; // prevent Ant Design auto-upload; file is held in state for manual submit
    }, [setUploadedFile]);

    const handleRemoveUpload = useCallback(() => {
        setUploadedFile(null);
        setUploadError(null);
        setFileValidationError(null);
    }, []);

    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const handlePreviewUpload = useCallback(() => {
        // Open the safe pdf.js-based preview modal. pdf.js renders to canvas and
        // never executes the PDF's embedded JavaScript, so an untrusted upload
        // cannot run scripts in our origin (no XSS) — unlike opening it inline.
        if (uploadedFile) setIsPreviewOpen(true);
    }, [uploadedFile]);

    const closePreview = useCallback(() => setIsPreviewOpen(false), []);

    // ── Public API ─────────────────────────────────────────────────────────────

    return {
        form,
        loading,
        fields,
        // Watched form values (used for disabled/conditional logic in JSX)
        legalEntity,
        businessUnit,
        functionDepartment,
        subFunction,
        specialty,
        roleType,
        jobGrade,
        workEnvironment,
        jobCode,
        roleReportingToSbUniqueId,
        isApexRole,
        // Options
        jobOptions,
        roleTypeOptions,
        jobGradeOptions,
        workEnvironmentOptions,
        legalEntities,
        reportingToOptions,
        roleReportingToOptions,
        // Cascade state
        availableBusinessUnits,
        availableFunctions,
        availableSubFunctions,
        availableSpecialties,
        // Derived
        filteredOfficeLocations,
        versionOptions,
        // Helpers
        isFieldDisabled,
        // Handlers
        handleJobChange,
        handleVersionChangeInput,
        onFinish,
        // Upload
        uploadedFile,
        uploadError,
        fileValidationError,
        handleBeforeUpload,
        handleRemoveUpload,
        handlePreviewUpload,
        isPreviewOpen,
        closePreview,
        uploadSubmitting,
    };
};

export default useJdForm;
